import { supabase, Profile, GuideProfile } from '../lib/supabase'

export class ProfileService {
  
  /**
   * Fetch profile data for the logged-in user
   */
  static async getMyProfile(): Promise<Profile | null> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      
      if (userError || !user) {
        throw new Error('User not authenticated')
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          // Profile doesn't exist, create a basic one
          return await this.createProfile(user.id, {
            username: user.email?.split('@')[0] || null,
            full_name: user.user_metadata?.full_name || null,
          })
        }
        throw error
      }

      return data
    } catch (error) {
      console.error('Error fetching profile:', error)
      throw error
    }
  }

  /**
   * Fetch public profile data for any user by their ID
   */
  static async getPublicProfile(userId: string): Promise<Profile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          user_id,
          username,
          full_name,
          avatar_url,
          bio,
          location,
          website,
          created_at,
          updated_at
        `)
        .eq('user_id', userId)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching public profile:', error)
      throw error
    }
  }

  /**
   * Update profile data for the logged-in user
   */
  static async updateProfile(profileData: Partial<Profile>): Promise<Profile> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      
      if (userError || !user) {
        throw new Error('User not authenticated')
      }

      const updates = {
        ...profileData,
        user_id: user.id,
        updated_at: new Date().toISOString(),
      }

      const { data, error } = await supabase
        .from('profiles')
        .upsert(updates)
        .eq('user_id', user.id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating profile:', error)
      throw error
    }
  }

  /**
   * Create a new profile for a user
   */
  static async createProfile(userId: string, profileData: Partial<Profile>): Promise<Profile> {
    try {
      const newProfile = {
        user_id: userId,
        username: profileData.username || null,
        full_name: profileData.full_name || null,
        avatar_url: profileData.avatar_url || null,
        bio: profileData.bio || null,
        location: profileData.location || null,
        website: profileData.website || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }

      const { data, error } = await supabase
        .from('profiles')
        .insert([newProfile])
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error creating profile:', error)
      throw error
    }
  }

  /**
   * Upload avatar image to Supabase Storage
   */
  static async uploadAvatar(file: File): Promise<string> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      
      if (userError || !user) {
        throw new Error('User not authenticated')
      }

      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}-${Math.random()}.${fileExt}`
      const filePath = `avatars/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file)

      if (uploadError) throw uploadError

      // Get public URL
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath)

      return data.publicUrl
    } catch (error) {
      console.error('Error uploading avatar:', error)
      throw error
    }
  }

  /**
   * Delete old avatar from storage
   */
  static async deleteAvatar(avatarUrl: string): Promise<void> {
    try {
      // Extract the file path from the URL
      const url = new URL(avatarUrl)
      const pathParts = url.pathname.split('/')
      const filePath = pathParts.slice(-2).join('/') // Get 'avatars/filename'

      const { error } = await supabase.storage
        .from('avatars')
        .remove([filePath])

      if (error) throw error
    } catch (error) {
      console.error('Error deleting avatar:', error)
      // Don't throw here as it's not critical
    }
  }
}

export class GuideProfileService {
  
  /**
   * Get guide profile for a user
   */
  static async getGuideProfile(userId: string): Promise<GuideProfile | null> {
    try {
      const { data, error } = await supabase
        .from('guide_profiles')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // No guide profile exists
        }
        throw error
      }

      return data
    } catch (error) {
      console.error('Error fetching guide profile:', error)
      throw error
    }
  }

  /**
   * Create or update guide profile
   */
  static async upsertGuideProfile(guideData: Partial<GuideProfile>): Promise<GuideProfile> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      
      if (userError || !user) {
        throw new Error('User not authenticated')
      }

      const updates = {
        ...guideData,
        user_id: user.id,
        updated_at: new Date().toISOString(),
      }

      const { data, error } = await supabase
        .from('guide_profiles')
        .upsert(updates)
        .eq('user_id', user.id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error upserting guide profile:', error)
      throw error
    }
  }

  /**
   * Get all active guides (public data)
   */
  static async getActiveGuides(): Promise<GuideProfile[]> {
    try {
      const { data, error } = await supabase
        .from('guide_profiles')
        .select(`
          *,
          profiles (
            username,
            full_name,
            avatar_url,
            location
          )
        `)
        .eq('is_active', true)
        .order('rating', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching active guides:', error)
      throw error
    }
  }
}